import { useEffect, useState } from 'react';
import { FileText, Loader2, VideoOff } from 'lucide-react';
import type { Source, SourceKind } from '@/lib/types';
import { repo } from '@/lib/repo';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

// Kinds whose payload is a stored media FILE (fetched via a signed GCS URL).
// Everything else is a text source rendered from the extracted text we already hold.
const FILE_KINDS: SourceKind[] = ['video', 'audio', 'pdf', 'image'];

type Loaded = { status: 'loading' } | { status: 'ready'; url: string } | { status: 'error'; message: string };

export function MediaViewer({
  source,
  open,
  onOpenChange,
}: {
  source: Source | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [media, setMedia] = useState<Loaded>({ status: 'loading' });

  const kind = source?.kind;
  const needsFile = !!kind && FILE_KINDS.includes(kind);

  useEffect(() => {
    if (!open || !source || !needsFile) return;
    let cancelled = false;
    setMedia({ status: 'loading' });
    repo
      .mediaUrl(source.topicId, source.id)
      .then(({ url }) => {
        if (!cancelled) setMedia({ status: 'ready', url });
      })
      .catch((e: unknown) => {
        if (!cancelled) setMedia({ status: 'error', message: e instanceof Error ? e.message : 'Could not load media' });
      });
    return () => {
      cancelled = true;
    };
  }, [open, source, needsFile]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="truncate pr-6">{source?.title ?? 'Source'}</DialogTitle>
          <DialogDescription className="font-mono text-[11px]">
            {kind}
            {source?.externalId ? ` · ${source.externalId}` : ''}
            {source?.capturedAt ? ` · ${source.capturedAt}` : ''}
            {source?.duration ? ` · ${source.duration}` : ''}
          </DialogDescription>
        </DialogHeader>
        {source && <MediaBody source={source} needsFile={needsFile} media={media} />}
      </DialogContent>
    </Dialog>
  );
}

function MediaBody({ source, needsFile, media }: { source: Source; needsFile: boolean; media: Loaded }) {
  // Text sources (article/transcript/social/data): show the extracted text we already hold.
  if (!needsFile) {
    const text = source.normalizedText?.trim() || source.rawText?.trim() || '';
    if (!text) {
      return <Empty icon={FileText} label="No extracted text for this source." />;
    }
    return (
      <div className="max-h-[70vh] overflow-auto rounded-md border border-border bg-muted/40 p-4">
        <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">{text}</p>
      </div>
    );
  }

  if (media.status === 'loading') {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (media.status === 'error') {
    return <Empty icon={VideoOff} label="No stored media file for this source." detail={media.message} />;
  }

  // media.status === 'ready'
  if (source.kind === 'video') {
    return (
      <video className="max-h-[70vh] w-full rounded-md border border-border bg-black" controls playsInline src={media.url} />
    );
  }
  if (source.kind === 'audio') {
    return <audio className="w-full" controls src={media.url} />;
  }
  if (source.kind === 'image') {
    return (
      <div className="max-h-[70vh] overflow-auto rounded-md border border-border bg-muted/40">
        <img className="mx-auto max-h-[70vh] w-auto" src={media.url} alt={source.title} />
      </div>
    );
  }
  // pdf
  return (
    <object data={media.url} type="application/pdf" className="h-[70vh] w-full rounded-md border border-border">
      <iframe title={source.title} src={media.url} className="h-[70vh] w-full rounded-md border border-border" />
    </object>
  );
}

function Empty({ icon: Icon, label, detail }: { icon: typeof FileText; label: string; detail?: string }) {
  return (
    <div className="flex h-48 flex-col items-center justify-center gap-2 text-center text-muted-foreground">
      <Icon className="h-8 w-8" />
      <p className="text-sm">{label}</p>
      {detail && <p className="max-w-md text-[11px] text-muted-foreground/70">{detail}</p>}
    </div>
  );
}
